import { Component } from '@angular/core';

import { Subscription } from 'rxjs';
import { Coverage } from '../../../interfaces/coverage.model';
import { Customer } from '../../../interfaces/customer.model';
import { ValidationError } from '../../../interfaces/validation-error.model';
import { BaseWidgetComponent } from '../../../shared/components/base.widget.component';
import { CoverageDetailsComponent } from '../coverage-details/coverage-details.component';
import { PersonalDetailsComponent } from '../personal-details/personal-details.component';

@Component({
  selector: 'app-coverage-tabs',
  imports: [PersonalDetailsComponent, CoverageDetailsComponent],
  templateUrl: './coverage-tabs.component.html',
  styleUrls: ['./coverage-tabs.component.scss'],
})
export class CoverageTabsComponent extends BaseWidgetComponent {
  override _dataSubscription: Subscription = new Subscription();
  customerTabs: string[] = [];

  override ngOnInit() {
    super.ngOnInit();
    this._initialiseCustomerTabs();

    this._dataSubscription = this._baseDataService.data$.subscribe(data => {
      if (data) {
        this.sharedPolicyData = structuredClone(data);
        this.updateCustomerTabs();
      }
    });
    this.setAccordions();

    if (this._dataSubscription) {
      this._baseSubscriptions.add(this._dataSubscription);
    }
  }

  get coverages(): Coverage[] {
    return this.sharedPolicyData?.coverages ?? [];
  }

  get customers(): Customer[] {
    return this.sharedPolicyData?.customers ?? [];
  }

  get coverageErrors(): ValidationError[] {
    return this.sharedPolicyData?.evaluationResponse?.errors ?? [];
  }

  get accordionStates(): { customer: number; accordions: number[] }[] {
    return this._baseDataService.get('expandedCoverages');
  }

  getOpenCoverageAccordions(customer: string): string[] {
    return (
      this.accordionStates
        .find((state: { customer: number; accordions: number[] }) => state.customer === parseInt(customer))
        ?.accordions.map(accordion => accordion.toString() ?? '') ?? []
    );
  }

  get customer(): Customer {
    return this.customers[this._getCustomerIndex(this.selectedTab)];
  }

  get hasSmokerError(): boolean {
    return this._hasErrorForCustomer(this._getCustomerIndex(this.selectedTab), 'dateOfBirth');
  }

  get hasDobError(): boolean {
    return this._hasErrorForCustomer(this._getCustomerIndex(this.selectedTab), 'dateOfBirth');
  }

  get smokerVal(): string {
    return this._baseGetValByCode('smoker', this.customer.smoker) ?? '';
  }

  get genderVal(): string {
    return this._baseGetValByCode('gender', this.customer.gender) ?? '';
  }

  get selectedTab(): string {
    return this._baseDataService.get('selectedTab');
  }

  protected setAccordions() {
    const accordions: { customer: number; accordions: number[] }[] = [];
    for (let i = 0; i < this.customerTabs.length; i++) {
      accordions.push({ customer: parseInt(this.customerTabs[i]), accordions: [] });
    }
    this._baseDataService.set('expandedCoverages', [...accordions]);
  }

  protected _getCustomerIndex(tab: string): number {
    return this.sharedPolicyData?.customers?.findIndex(customer => customer.number === tab) ?? 0;
  }

  protected _initialiseCustomerTabs() {
    if (this.sharedPolicyData?.customers) {
      this.customerTabs = this.sharedPolicyData?.customers?.map(
        (_customer: Customer) => _customer.number?.toString() ?? '',
      );
      if (this.customerTabs.length > 0) {
        this.selectTab(this.customerTabs[0]);
      }
    }
  }

  /**
   * Updates the list of customer tabs based on the current state of sharedPolicyData.
   *
   * @returns {void} Does not return a value.
   */
  protected updateCustomerTabs() {
    if (this.sharedPolicyData?.customers) {
      if (this.customerTabs.length > 0) {
        if (this.customerTabs.length > this.sharedPolicyData?.customers.length) {
          const customerNumbers = this.sharedPolicyData?.customers.map(customer => customer.number);
          this.customerTabs = this.customerTabs.filter(str => customerNumbers.includes(str));
        }
        if (this.selectedTab != null && this.customerTabs.includes(this.selectedTab)) {
          this.selectTab(this.selectedTab);
        } else {
          this.selectTab(this.customerTabs[0]);
        }
      }
    }
  }

  /**
   * Returns any validation error messages for a specific customer, if there are any.
   *
   * @param {number} cusIndex - The index of the customer in the customers array.
   * @returns {string[]} An array of error messages relating to the specified customer.
   */
  protected _showCustomerErrors(cusIndex: number): string[] {
    const errors: string[] = [];

    // Check if evaluationResponse and errors exist
    if (this.sharedPolicyData?.evaluationResponse?.errors) {
      this.sharedPolicyData.evaluationResponse.errors.forEach((error: ValidationError) => {
        const loweredPaths = error.path.map(p => p.toLowerCase());
        const hasCustomer = loweredPaths.some(p => p.includes(`customers[${cusIndex}]`));

        if (hasCustomer) {
          errors.push(error.message);
        }
      });
    }

    return errors;
  }

  /**
   * Checks if a specified customer has a validation error for a given property.
   *
   * @param {number} cusIndex - The index of the customer within the customers array.
   * @param {string} property - The property to check for validation errors (e.g., 'dateOfBirth').
   * @returns {boolean} True if the error exists for the specified property and customer; otherwise, false.
   */
  protected _hasErrorForCustomer(cusIndex: number, property: string): boolean {
    if (!this.sharedPolicyData?.evaluationResponse?.errors) {
      return false; // No errors to check against
    }

    const customerPathToCheck = `customers[${cusIndex}].${property}`.toLowerCase();

    const conditionMet = this.sharedPolicyData.evaluationResponse.errors.some((error: ValidationError) =>
      error.path.some(p => p.toLowerCase().startsWith(customerPathToCheck)),
    );

    return conditionMet;
  }
}
